import moment from 'moment'
import debounce from 'lodash/debounce.js'
import MediaEntry from '@root/backend/databases/media-entry-base.js'
import { RoomEventTypes, MediaEventTypes } from '@root/backend/databases/events.js'
import AppBus from '@C/bus/app-bus.vue'
import Emittery from 'emittery'

class VideoControls {
  constructor (component, videoSelector) {
    Object.assign(this, {
      component,
      videoSelector
    })
  }

  get plyr () {
    return this.component.plyr
  }

  updateMediaEntry (json) {
    // Captions will be an object here whereas mediaEntry.captions is an array
    // So strip it out before updating mediaEntry
    const { captions, ...props } = json
    Object.assign(this.component.mediaEntry, props)
    this.component.mediaEntry.captions.push(captions)
  }

  async play () {
    return this.plyr.play()
  }

  async pause () {
    return this.plyr.pause()
  }

  async seek (pos) {
    if (pos !== await this.currentTime()) {
      this.plyr.currentTime = (pos / 1e3)
    }
  }
  async paused () {
    const status = await this.status()
    return status === 'paused'
  }
  async status () {
    if (typeof this.plyr.status === 'function') {
      return this.plyr.status()
    }
    let paused
    if (typeof this.plyr.paused === 'function') {
      paused = await this.plyr.paused()
    } else {
      paused = this.plyr.paused
    }
    if (paused) {
      return 'paused'
    }
    return 'playing'
  }
  async currentTime () { return (await this.plyr.currentTime) * 1e3 }

  async getSource () { return this.plyr.source }

  async duration () { return (await this.plyr.duration) * 1e3 }

  async sourceChange (json) {
    const { component } = this
    const { newMedia: mediaEntry } = json
    try {
      // Emit an event on the component signalling source-change
      if (mediaEntry.lastPlayTimestampMS > 0 || mediaEntry.position > 0) {
        component.$once('plyr-ready', e => this.onCanPlay(e, json))
      }
      AppBus.$emit('sourceChange', json)
    } catch (e) {
      console.error(e)
    }
  }

  async addCaptions (json) {
    this.component.addCaptions(json.captions)
  }

  async onCanPlay (e, json) {
    const { detail: { plyr } } = e
    const { newMedia: mediaEntry, resyncMedia = true } = json
    let { position, lastPlayTimestampMS, status } = mediaEntry
    if (status === 'playing') {
      position += (moment.utc().valueOf() - lastPlayTimestampMS) + 300 // A fixed offset to account for buffering
    }
    const duration = await this.duration()
    // Don't seek past the duration of the video
    // This is done to ensure that this seek doesn't trigger a race with regard to playlist entry loading
    if (position && position !== 0 && position < duration) {
      // seek on 'canplay' seems to cause video to start playing
      // so add a listener and ensure video state is correct
      plyr.once('seeked', () => {
        if (status === 'playing') {
          this.play()
        } else {
          this.pause()
        }
      })
      await this.seek(position)
      // XXX: Another hack to ensure people are sync'd up correctly
      // TODO: The JSON that the backend is going to send us is (probably) the same as the one we already have
      // Just do a local re-sync rather than ask the server to tell us to resync
      if (resyncMedia) {
        setTimeout(() => {
          AppBus.$emit('resync-media', {
            mediaEntry,
            options: {
              noMediaUpdate: true,
              noSourceChange: true
            }
          })
        }, 1000)
      }
    }
  }
}

const Videoshare = (function () {
  const self = new Emittery()

  self.plyrs = {}
  self.plyrMediaSelector = '.plyr--setup'
  self.videoControls = {}
  self.extensionVersion = ''

  /**
   * Each video object must expose some basic functions which videoshare.js
   * will then use to control playback. Currently, the API requires video objects
   * to expose the following methods:
   *  - play
   *  - pause
   *  - seek (ms)
   *  - currentTime (ms)
   *  - sourceChange
   */
  self.setupPlyrVideoControls = function (component, videoSelector, overrides) {
    const videoControls = new VideoControls(component, videoSelector)
    self.registerVideoControls(videoControls, videoSelector)
    return videoControls
  }

  self.getPlyr = function (videoSelector) {
    return self.plyrs[videoSelector]
  }

  // This method is used by the extension to register controls
  self.registerVideoControls = (videoControls, videoSelector) => {
    self.videoControls[videoSelector] = videoControls
    const { component: { plyr } } = videoControls
    self.plyrs[videoSelector] = plyr
    console.log(`[videoshare.js] Registered videoControls selector=${videoSelector}`)
    self.emit('registered-video-controls', { videoSelector })
  }

  self.onSourceChange = async function (videoSelector, newMedia, props, forceSourceChange = false) {
    const { component = {} } = self.videoControls[videoSelector]
    const { mediaEntry: currentMediaEntry = {} } = component
    const { videoData: currentVideoData = {} } = currentMediaEntry
    const { videoData: newVideoData = {} } = newMedia
    let isVideoNew = false
    if (forceSourceChange || (currentVideoData.source === 'plex' || currentVideoData.treatEachVideoAsNew)) {
      isVideoNew = true
    }
    if (!forceSourceChange) {
      if (currentMediaEntry.videoURL === newMedia.videoURL ||
          (!isVideoNew && currentVideoData.topURL !== undefined && currentVideoData.topURL === newVideoData.topURL)) {
        try {
          if (component.data.type === 'iframe') {
            // This logic is only valid from v2.2.12 and above
            const needsUpdate = component.needsExtensionUpdate('2.2.12', component.extensionVersion)
            if (!needsUpdate) {
              // We have a version of the extension that supports checking iframe URL
              const postTo = window.tsExtGetPostTo && window.tsExtGetPostTo()
              // For whatever reason, we may not have postTo. Bail if we don't
              if (postTo) {
                const iframeURL = await postTo(component.$refs.iframe.contentWindow, { // eslint-disable-line no-unused-vars
                  action: 'get-url'
                }, true)
                if (newMedia.videoURL !== iframeURL) {
                  await postTo(component.$refs.iframe.contentWindow, {
                    action: 'force-url',
                    url: newMedia.videoURL
                  })
                }
              }
            }
          }
        } catch (e) {
        }
        console.warn(`[videoshare.js]: Skipping ${RoomEventTypes.ROOM_SOURCE_CHANGE} since [${videoSelector}] videoURLs are identical`)
        return false
      }
    }
    let msg = 'Requesting server for source change: ' + videoSelector + ' - ' + JSON.stringify(newMedia)
    if (newMedia.embedSource) {
      msg += ' - embedSource:' + newMedia.embedSource
    }
    const dict = {
      videoSelector,
      newMedia
    }
    if (props) {
      Object.assign(dict, props)
    }
    // We have a try-catch here because keys.general.allowCookies will not be available if extension versions < v2.1.5.42
    try {
      const { $store: { getters: { extensionConfig: { settings: { data: extensionSettings, keys } } } } } = this
      if (!extensionSettings[keys.general.allowCookies]) {
        // Get rid of headers
        newMedia.videoData = JSON.parse(JSON.stringify(newVideoData))
        newMedia.videoData.headers = []
      }
    } catch (e) {
    }
    // TODO: Check whether we're allowed to send headers
    console.log(`[videoshare.js]: ${msg} dict=${JSON.stringify(dict)}`)
    self.socket.emit(RoomEventTypes.ROOM_SOURCE_CHANGE, dict)
    return true
  }

  self.onMediaUpdate = function (newSelector, props) {
    self.socket.emit(RoomEventTypes.ROOM_MEDIA_UPDATE, { newSelector, ...props })
  }

  self.onInteractiveChoice = async (e, component, json) => {
    return Videoshare.eventHelper(e, component, MediaEventTypes.MEDIA_INTERACTIVE_CHOICE, json)
  }

  self.getPlyrFromEvent = function (e) {
    return e.plyr
  }
  self.eventHelper = async (e, component, requestName, extras = {}) => {
    try {
      const { plyr } = component
      let position = (await plyr.currentTime) * 1000
      if (window.offset) {
        position = Math.max(0, position - window.offset)
      }
      let json = {
        videoSelector: component.videoSelector,
        position: position,
        roomName: self.roomName,
        mediaEntry: { uuid: component.mediaEntry.uuid }
      }
      // XXX: What is this?
      if (component.$parent.currentVideo && component.$parent.currentVideo.extraProps) {
        Object.assign(json, component.$parent.currentVideo.extraProps)
      }
      try {
        component.$emit('media-action', { requestName, ...extras })
      } catch (e) {
      }

      json = Object.assign({}, json, extras)
      self.socket.emit(requestName, json)
    } catch (e) {
      console.error(e)
    }
  }
  self.onPlay = async (e, component, extras) => {
    return Videoshare.eventHelper(e, component, MediaEventTypes.MEDIA_PLAY, extras)
  }
  self.onPause = async (e, component, extras) => {
    return Videoshare.eventHelper(e, component, MediaEventTypes.MEDIA_PAUSE, extras)
  }
  self.onSeek = debounce(async (e, component, extras) => {
    // Seek is a little special since we're sending across the new seeked-to time
    const { plyr } = component
    const position = (await _getTargetTime(plyr, e)) * 1000
    return self.eventHelper(e, component, MediaEventTypes.MEDIA_SEEK, { ...extras, position })
  }, 10)
  self.onAddCaptions = async (e, component, extras) => {
    return self.eventHelper(e, component, MediaEventTypes.MEDIA_ADD_CAPTIONS, extras)
  }
  self.updateController = (videoSelector) => {
    const { videoControls: { [videoSelector]: videoController } } = self
    self.currentVideoController = videoController
  }

  self.videoControlCheck = async (json, shouldPause = true) => {
    const { videoSelector } = json
    const videoController = self.videoControls[videoSelector]
    if (videoController !== self.currentVideoController) {
      if (self.currentVideoController) {
        try {
          const plyr = self.getPlyr(self.currentVideoController.videoSelector)
          if (shouldPause) {
            const dummyJson = {
              videoSelector: self.currentVideoController.videoSelector,
              position: (await plyr.currentTime) * 1000
            }
            self.currentVideoController.pause(dummyJson).then(() => {}).catch(() => {})
          }
        } catch (e) {
          console.error(e, { json, 'currentVideoController.videoSelector': self.currentVideoController.videoSelector })
        }
      } else {
        self.bus.$emit('no-controller', json)
      }
      self.updateController(videoSelector)
    }
  }

  self.doSourceChange = async (json, cb) => {
    // source-change shouldn't be changing video selector or pausing videos
    // That's the job of media update
    const { videoSelector } = json
    const videoController = self.videoControls[videoSelector]
    json.newMedia = new MediaEntry(json.newMedia)
    json.newMedia = self.convertMediaEntryIfNeeded(videoSelector, json.newMedia)

    if (!videoController) {
      console.error(`[videoshare.js]: No controller found  for videoSelector: ${videoSelector}`)
      self.bus.$emit('sourceChange', json)
    } else {
      const { captions = [] } = json
      json.newMedia.captions = captions
      await videoController.sourceChange(json)
      cb && cb()
    }
  }

  self.convertMediaEntryIfNeeded = (videoSelector, newMedia) => {
    if (videoSelector !== 'web') {
      return newMedia
    }
    // handling web mediaEntry conversion
    if (newMedia && newMedia.videoData) {
      if (newMedia.videoData.strategies) {
        // this is v2 media entry
        if (self.extensionVersion.startsWith('3.')) {
        // this needs conversion
          return self.convertV2ToV3(newMedia)
        }
      } else if (newMedia.videoData.module) {
        // this is a v3 media entry
        if (!self.extensionVersion.startsWith('3.')) {
        // this needs conversion
          return self.convertV3ToV2(newMedia)
        }
      }
    }
    return newMedia // no conversion needed
  }
  self.convertV2ToV3 = (newMedia) => {
    // videoData.strategies in V2 == videoData.module.config.strategies in V3
    const { strategies } = newMedia.videoData // this object should be available since we've made checks before calling this function
    newMedia.videoData.module = newMedia.videoData.module || {}
    newMedia.videoData.module.config = newMedia.videoData.module.config || {}
    newMedia.videoData.module.config.strategies = strategies
    return newMedia
  }

  self.convertV3ToV2 = (newMedia) => {
    // videoData.module.config.strategies in V3 == videoData.strategies in V2

    let strategies = newMedia.videoData.module.config.strategies || null
    newMedia.videoData.strategies = strategies

    // TODO: v2's videoData also needs numSegments, referer, origin, fromTabId, module.loadMediaFromModule
    Object.assign(newMedia, {
      referer: newMedia.videoData.topURL,
      origin: new URL(newMedia.videoData.topURL).origin
    })

    return newMedia
  }

  self.doMediaUpdate = async ({ newSelector }) => {
    async function __videoControlSourceChange (newSelector) {
      const videoController = self.videoControls[newSelector]
      if (videoController !== self.currentVideoController) {
        if (self.currentVideoController) {
          console.log(`[videoshare.js]: currentVideoController=${self.currentVideoController.videoSelector} newVideoController=${newSelector}`)
          try {
            await self.currentVideoController.pause()
          } catch (e) {
            console.log('[videoshare.js]: [media-update]: Failed to pause currentVideoController')
            console.log(e && e.stack)
          }
        }
        await self.videoControlCheck({ videoSelector: newSelector }, false)
      }

      try {
        // Get selector from component
        const { component } = self.currentVideoController
        const { videoSelector } = self.currentVideoController.component
        // Get the component
        if (component) {
          component.earlyInitializePromise && await component.earlyInitializePromise
          if (!component.initialized) {
            await component.initialize()
          }
          component.$store.commit('currentSelector', videoSelector)
        } else {
          console.error(`No component found for selector: ${newSelector}`)
        }
      } catch (e) {
        // do nothing
        console.error(e)
      }
    }
    console.log(`[videoshare.js]: Received ${RoomEventTypes.ROOM_MEDIA_UPDATE}..newSelector=${newSelector}`)

    // FIXME: This whole logic is kinda broken. It all fails if preInitialize
    // is set to false by default. Fix this
    // Set the current videoController, and call its RoomEventTypes.ROOM_SOURCE_CHANGE (if needed)
    const videoController = self.videoControls[newSelector]
    if (!videoController) {
      // videoController not found. This is either an error or we're waiting for videoControls
      // to be registered or we're inside the extension and have no idea about this videoSelector.
      // if ((window.extensionDivID && json.divID !== window.extensionDivID) && self.socket.secondarySocket) {
      //   // We're inside extension.
      //   // Just call __videoControlSourceChange()
      //   __videoControlSourceChange()
      // } else {
      //   // Bind to the event.
      //   document.addEventListener('all-videoshare-handlers-registered', __videoControlSourceChange)
      // }
      console.log(`[videoshare.js]: Waiting for video controller to be regsitered. videoSelector=${newSelector}`)
      await new Promise(resolve => self.on('registered-video-controls', ({ videoSelector }) => {
        if (videoSelector === newSelector) {
          resolve()
        }
      }))
      console.log(`[videoshare.js]: Video controller regsitered. videoSelector=${newSelector}`)
      // self.bus.$emit('no-controller', { videoSelector: newSelector })
    }
    return __videoControlSourceChange(newSelector)
  }

  self.registerVideoShareEvents = function () {
    self.socket.on(RoomEventTypes.ROOM_SOURCE_CHANGE, self.doSourceChange)

    self.socket.on(MediaEventTypes.MEDIA_PLAY, async (json) => {
      console.log('[videoshare.js]: Server says play: ' + JSON.stringify(json))
      await self.videoControlCheck(json)
      if (window.offset) {
        json.position += window.offset
      }
      // Position in ms
      const { position } = json
      // console.log('[videoshare.js]: Server says play at pos:' + position);
      if (!self.currentVideoController) {
        return
      }
      try {
        await self.currentVideoController.updateMediaEntry(json, MediaEventTypes.MEDIA_PLAY)
      } catch (e) {
        console.error(e)
      }
      try {
        self.currentVideoController.component.mediaEntry.lastPlayTimestampMS = moment().utc().valueOf()
      } catch (e) {
        console.error(`Failed to update lastPlayTimestampMS locally: ${e.message}`, e)
      }
      const currentTime = await self.currentVideoController.currentTime()
      const currentTimeMS = currentTime * 1e3
      if (Math.abs(currentTimeMS - position) > 100) {
        await self.currentVideoController.seek(position)
      }
      await self.currentVideoController.play()
      const { extras } = json
      if (!extras) {
        return
      }
      const { resync } = extras
      if (resync) {
        AppBus.$emit('resync-media', {
          mediaEntry: self.currentVideoController.component.mediaEntry,
          options: {
            noMediaUpdate: true,
            noSourceChange: true
          }
        })
      }
    })

    self.socket.on(MediaEventTypes.MEDIA_PAUSE, async (json) => {
      console.log('[videoshare.js]: Server says pause: ' + JSON.stringify(json))
      await self.videoControlCheck(json)
      if (!self.currentVideoController) {
        return
      }
      if (window.offset) {
        json.position += window.offset
      }
      // Position in ms
      const { position } = json
      // console.log('[videoshare.js]: Server says pause at pos: %.2f (%.2f)', position, (currentVideoController.currentTime()));
      try {
        await self.currentVideoController.updateMediaEntry(json)
      } catch (e) {}
      await self.currentVideoController.pause()
      const currentTime = await self.currentVideoController.currentTime()
      const currentTimeMS = currentTime * 1e3
      if (Math.abs(currentTimeMS - position) > 100) {
        await self.currentVideoController.seek(position)
      }
    })

    self.socket.on(MediaEventTypes.MEDIA_SEEK, async (json) => {
      console.log('[videoshare.js]: Server says seek: ' + JSON.stringify(json))
      await self.videoControlCheck(json)
      if (!self.currentVideoController) {
        return
      }
      if (window.offset) {
        json.position += window.offset
      }
      // Position in ms
      const { position } = json
      // console.log('[videoshare.js]: Server says seek to pos:' + position);
      try {
        await self.currentVideoController.updateMediaEntry(json)
      } catch (e) {}
      if (Number((await self.currentVideoController.currentTime())) !== Number(position)) {
        await self.currentVideoController.seek(position)
      }
    })

    self.socket.on(MediaEventTypes.MEDIA_INTERACTIVE_CHOICE, async (json) => {
      console.log('[videoshare.js]: Server says interactiveChoice: ' + JSON.stringify(json))
      await self.videoControlCheck(json)
      if (!self.currentVideoController) {
        return
      }
      try {
        await self.currentVideoController.updateMediaEntry(json)
      } catch (e) {}
      await self.currentVideoController.interactiveChoice(json)
    })

    self.socket.on(MediaEventTypes.MEDIA_ADD_CAPTIONS, async (json) => {
      console.log('[videoshare.js]: Server says addCaption')
      await self.videoControlCheck(json)
      if (!self.currentVideoController) {
        return
      }
      try {
        await self.currentVideoController.updateMediaEntry(json)
      } catch (e) {}
      await self.currentVideoController.addCaptions(json)
    })

    self.socket.on(RoomEventTypes.ROOM_MEDIA_UPDATE, self.doMediaUpdate)

    self.socket.on(RoomEventTypes.ROOM_MEDIA_RESYNC, async ({ videoSelector, mediaEntry }) => {
      await self.videoControlCheck({ videoSelector })
      const { position: lastPosition, lastPlayTimestampMS, status } = mediaEntry
      try {
        await self.currentVideoController.updateMediaEntry(mediaEntry)
      } catch (e) {}
      const videoController = self.videoControls[videoSelector]
      if (!videoController) {
        return self.bus.$emit('no-controller', { videoSelector })
      }
      const isPlaying = status === 'playing'
      const currentVideoPosition = await videoController.currentTime()
      const now = moment().utc().valueOf()
      let finalSeekPosition = lastPosition
      if (isPlaying && lastPlayTimestampMS !== -1) {
        finalSeekPosition += (now - lastPlayTimestampMS)
      }
      const diff = Math.abs((currentVideoPosition * 1e3) - finalSeekPosition)
      if (diff > 100) { // If it varies by more a threshold
        if (isPlaying) {
          finalSeekPosition += 300 // To account for buffering
        }
        await videoController.seek(finalSeekPosition) // Add any delay between _then_ and now
      }
      isPlaying ? await videoController.play() : await videoController.pause()
    })
  }

  async function _getTargetTime (plyr, input) {
    if (typeof input === 'object' && (input.type === 'input' || input.type === 'change')) {
      return ((input.target.value / input.target.max) * (await plyr.duration))
    } else {
      // We're assuming its a number
      return Number(input) / 1e3
    }
  }

  self.setup = function (socket, roomName, bus) {
    self.socket = socket
    self.roomName = roomName
    self.bus = bus
    self.registerVideoShareEvents()
    console.log('[videoshare.js]: Videoshare library initialized')
  }

  self.getEvents = function () {
    return {
      play: MediaEventTypes.MEDIA_PLAY,
      pause: MediaEventTypes.MEDIA_PAUSE,
      seek: MediaEventTypes.MEDIA_SEEK,
      interactiveChoice: MediaEventTypes.MEDIA_INTERACTIVE_CHOICE,
      sourceChange: RoomEventTypes.ROOM_SOURCE_CHANGE
    }
  }

  return self
}())

// FIXME: Ideally, this shouldn't be needed
window.Videoshare = Videoshare

export default Videoshare

const setup = Videoshare.setup.bind(Videoshare)
export {
  setup
}
